import React, { Fragment, memo, useEffect } from 'react';

import PropTypes from 'prop-types';

import clsx from 'clsx';
import _get from 'lodash/get';

import { useDevice } from '@bonnet/next/device';

import { useFeatures } from '@atc/features';
import { ClientOnly } from '@atc/react-client-only';

import { Image, Text } from 'reaxl';
import {
    OwnerAddress,
    OwnerChat,
    OwnerPhoneNumber,
} from 'reaxl-molecules';
import { KBBDealerRatings } from 'reaxl-ratings-reviews';

import LongHaulDeliveryCard from '@/components/LongHaulDeliveryCard';
import MarketExtensionCard from '@/components/MarketExtensionCard';
import DealerTitle from '@/components/vehicledetails/DealerTitle';

/**
 *  The OwnerDetailsSnapshot displays basic dealer information and contact links.
 *  For private owner, please use PrivateOwnerDetails
 */

function PeaceOfMindCard({ addSpacing, cmp, imgSrc, headline, description }) {
    return (
        <div
            className={clsx('display-flex margin-vertical-2 align-items-center', { 'margin-right-5': addSpacing })}
            data-cmp={cmp}
        >
            <Image
                className="margin-right-3"
                src={imgSrc}
                width={32}
                height={32}
                alt=""
            />
            <div>
                <Text
                    weight="bold"
                    componentClass="div"
                >
                    {headline}
                </Text>
                <Text>
                    {description}
                </Text>
            </div>
        </div>
    );
}

const divider = <hr className="margin-vertical-2" />;

const OwnerDetailsSnapshot = memo(({
    chatClickHandler,
    className,
    defaultShowModal,
    enableRatingImpression,
    extendedHomeDeliveryProps = {},
    hideDealerName = false,
    listing,
    phoneClickHandler,
    showAddress = false,
    trimSelected,
    selectedVrsData = {},
    handleEmptySnapshot = () => { },
}) => {
    const device = useDevice();
    const isXs = _get(device, 'is.xs', false);
    const isLessThanMd = _get(device, 'lessThan.md', false);

    const {
        call: [isCallEnabled],
        chat: [,
            {
                providers: chatProviders,
                enable_mobile_chat: enableMobileChat,
            },
        ],
        vdp_extended_home_delivery: [useExtendedHomeDelivery],
        owner_snapshot_peace_of_mind: [ownerSnapshotPeaceOfMindCards],
        esntial_confident_purchase: [isEsntialConfidentPurchase],
    } = useFeatures([
        'call',
        'chat',
        'vdp_extended_home_delivery',
        'owner_snapshot_peace_of_mind',
        'esntial_confident_purchase',
    ]);

    const { marketExtension = {}, marketExtDescription, owner = {}, isLongHaulDelivery, ecommWebsite = {}, esntial, returnPolicy = {}, dealerConfig = {}, warranties = {} } = listing || {};
    const { chat = {}, name, website = {}, location, phone = {}, rating, distanceFromSearch } = owner;
    const { shippingCost, description = marketExtDescription, isMarketExtListing = false, isVirtualDealer } = marketExtension;

    const showMoneyBackGuarantee = ownerSnapshotPeaceOfMindCards && returnPolicy?.days && returnPolicy?.miles && ecommWebsite?.href;
    const showLimitedWarranty = ownerSnapshotPeaceOfMindCards && warranties?.limitedWarranty?.summary && ecommWebsite?.href;

    const showPhoneLink = isCallEnabled && !!phone?.value && (!hideDealerName || !isXs);
    const showChatLink = (!isXs || enableMobileChat) && !!chat?.provider; // Gubagoo doesn't render a link here, but logic should still be true
    const showPipe = showPhoneLink && showChatLink && chatProviders?.includes(chat?.provider); // AE and Gubagoo are the only providers for now, and Gubagoo doesn't render a link
    const showMarketExtension = isMarketExtListing && (isVirtualDealer || !!location);
    const showPeaceOfMindCard = !(isEsntialConfidentPurchase && !!esntial && !esntial?.deemphasized) && (showMoneyBackGuarantee || showLimitedWarranty);

    const showOwnerAddress = showAddress && !!location && (!!description || !isVirtualDealer);

    if (hideDealerName) {
        phone.linkText = "Get seller's phone number";
    }

    const analyticsData = {
        inventoryId: listing.id,
        ownerId: owner.id,
        placement: 'snapshot',
        selectedVrsData,
        trimSelected,
    };

    const marketExtensionCardProps = { description, shippingCost };
    const showExtendedDeliveryCard = !!distanceFromSearch && distanceFromSearch > 50 && useExtendedHomeDelivery && isLongHaulDelivery;

    let displayOwnerSnapshotSiteLink = true;
    if (!!dealerConfig && dealerConfig?.displayOwnerSnapshotSiteLink === false) {
        displayOwnerSnapshotSiteLink = false;
    }

    const shouldRenderDealerLink = website.href && displayOwnerSnapshotSiteLink;

    let dealerTitleLinkProps = {};
    if (shouldRenderDealerLink) {
        dealerTitleLinkProps = {
            isLink: true,
            href: website?.href,
            analyticsData,
            componentClass: 'h2',
        };
    }

    const snapshot = [];

    useEffect(() => {
        // Handle not render Panel when snapshot is empty
        if (snapshot.length === 0) {
            handleEmptySnapshot();
        }
    }, [snapshot.length, handleEmptySnapshot]);

    if (!hideDealerName) {
        snapshot.push((
            <Fragment key="snapshot-dealer">
                <DealerTitle
                    {...dealerTitleLinkProps}
                >
                    {name}
                </DealerTitle>
                {(!!rating?.value) && (
                    <KBBDealerRatings
                        className="margin-top-2"
                        owner={owner}
                        widgetType="inline"
                        showCount
                        enableImpression={enableRatingImpression}
                        defaultShowModal={defaultShowModal}
                    />
                )}
            </Fragment>
        ));
    }

    if (showPhoneLink || showChatLink) {
        snapshot.push((
            <div
                className={clsx('display-flex align-items-center flex-wrap', { 'margin-top-1': !hideDealerName })}
                style={{ minHeight: '40px' }}
                key="phone_chat_link"
            >
                {showPhoneLink && (
                    <OwnerPhoneNumber
                        phone={phone}
                        isCallable={isLessThanMd}
                        analyticsData={analyticsData}
                        phoneClickHandler={phoneClickHandler}
                        className="text-size-300 text-size-md-400"
                    />
                )}
                {showChatLink && (
                    <ClientOnly>
                        {showPipe && <div className="margin-left-2">|</div>}
                        <OwnerChat
                            {...chat}
                            analyticsData={analyticsData}
                            chatClickHandler={chatClickHandler}
                            className="padding-0 text-bold"
                            listing={listing}
                            key="chat-cta-owner-chat"
                            merchantId={owner.id}
                            uiContext="iconButton"
                        />
                    </ClientOnly>
                )}
            </div>
        ));
    }

    if (showOwnerAddress) {
        snapshot.push((
            <OwnerAddress
                key="ownerAddress"
                uiContext="default"
                location={location}
                analyticsData={analyticsData}
                wrapperComponent="div"
                wrapperProps={{ className: 'margin-top-2' }}
            />
        ));
    }

    if (showExtendedDeliveryCard || showMarketExtension) {
        snapshot.push((
            <Fragment key="delivery">
                {snapshot.length !== 0 && divider}
                {showExtendedDeliveryCard ? (
                    <LongHaulDeliveryCard
                        listingId={listing.id}
                        uiContext="default"
                        {...extendedHomeDeliveryProps}
                    />
                ) : (
                    <MarketExtensionCard
                        uiContext="default"
                        {...marketExtensionCardProps}
                    />
                )}
            </Fragment>
        ));
    }

    if (showPeaceOfMindCard) {
        snapshot.push((
            <div
                className="display-flex flex-wrap"
                key="peace-of-mind"
            >
                {showMoneyBackGuarantee && (
                    <PeaceOfMindCard
                        addSpacing
                        cmp="money-back-guarantee-card"
                        imgSrc="https://content.autotrader.com/content/dam/autotrader/listing/srpvdp/money-back-guarantee.svg"
                        headline={`${returnPolicy.days}-Day Guarantee`}
                        description={`Up to ${returnPolicy.miles} Miles`}
                    />
                )}
                {showLimitedWarranty && (
                    <PeaceOfMindCard
                        cmp="limited-warranty-card"
                        imgSrc="https://content.autotrader.com/content/dam/autotrader/listing/srpvdp/factory-warranty.svg"
                        headline={warranties.limitedWarranty.summary}
                        description="Limited Warranty"
                    />
                )}
            </div>
        ));
    }

    return (
        <div
            data-cmp="ownerDetailsSnapshot"
            className={className}
        >
            {snapshot}
        </div>
    );
});

OwnerDetailsSnapshot.propTypes = {
    /**
     * The className to apply to the root node
     */
    className: PropTypes.string,
    /**
     * Standard listing object
     */
    listing: PropTypes.object,
    /**
      * The callback function fires when owner phone is called replacing fireTelmetricsTag
      */
    phoneClickHandler: PropTypes.func,
    /**
     * Object containing props for LongHaulDeliveryCard
    */
    extendedHomeDeliveryProps: PropTypes.object,
    /**
     * Boolean to indicate that set open modal inital value
    */
    defaultShowModel: PropTypes.bool,
    /**
     * Boolean to show address
    */
    showAddress: PropTypes.bool,
};

export default OwnerDetailsSnapshot;
